import { mapActions } from 'vuex'
import {
  formatVersionHistoryTimeAgo,
  formatVersionHistoryTimestamp,
  resolveUserTimeZone
} from '@/utils/versionHistoryTime'

export default {
  name: 'VersionHistory',
  props: {
    articleId: {
      type: [Number, String],
      required: true
    },
    contentplanId: {
      type: [Number, String],
      required: true
    },
    currentContent: {
      type: String,
      default: ''
    }
  },
  data () {
    return {
      versions: [],
      selectedVersion: null,
      selectedVersionContent: '',
      isLoading: {
        versions: false,
        version: false
      }
    }
  },
  computed: {
    userTimeZone () {
      return resolveUserTimeZone()
    },
    formattedVersions () {
      return this.versions
        .filter(version => !version.is_current)
        .map(version => ({
          ...version,
          timeAgo: this.formatTimeAgo(version.created_at),
          timestampLabel: this.formatTimestamp(version.created_at),
          timestampTitle: this.formatTimestampTitle(version.created_at)
        }))
    }
  },
  methods: {
    ...mapActions([
      'articleVersionsGet'
    ]),

    async loadVersions () {
      if (!this.articleId || !this.contentplanId) return

      this.isLoading.versions = true
      try {
        this.versions = await this.articleVersionsGet({
          contentplanId: this.contentplanId,
          articleId: this.articleId
        })
      } catch (error) {
        console.error('Failed to load versions:', error)
      } finally {
        this.isLoading.versions = false
      }
    },

    async selectVersion (version) {
      if (this.selectedVersion?.id === version.id) {
        // Deselect if clicking the same version
        this.selectedVersion = null
        this.selectedVersionContent = ''
        this.$emit('preview', null, false) // Clear preview
        return
      }

      this.selectedVersion = version
      this.isLoading.version = true
      // Show loading state in main editor
      this.$emit('preview', null, true)

      try {
        const versionData = await this.articleVersionsGet({
          contentplanId: this.contentplanId,
          articleId: this.articleId,
          versionId: version.id
        })

        // User might have cancelled preview before the request finished
        if (!this.selectedVersion || this.selectedVersion.id !== version.id) {
          return
        }

        this.selectedVersionContent = versionData.final || ''
        console.log('Version loaded, content length:', this.selectedVersionContent.length)
        console.log('First 200 chars:', this.selectedVersionContent.substring(0, 200))
        // Emit preview event to show in main editor
        this.$emit('preview', this.selectedVersionContent, false)
      } catch (error) {
        console.error('Failed to load version:', error)
        this.selectedVersionContent = ''
        this.$emit('preview', null, false)
      } finally {
        this.isLoading.version = false
      }
    },

    restoreVersion () {
      if (!this.selectedVersion || !this.selectedVersionContent) return
      this.$emit('restore', this.selectedVersionContent)
      this.selectedVersion = null
      this.selectedVersionContent = ''
      this.closeHistory()
    },

    cancelPreview () {
      if (this.isLoading.version) {
        this.isLoading.version = false
      }
      this.selectedVersion = null
      this.selectedVersionContent = ''
      this.$emit('preview', null, false)
    },

    closeHistory () {
      // Clear any preview when closing
      this.cancelPreview()
      this.$emit('close')
    },

    formatTimeAgo (timestamp) {
      return formatVersionHistoryTimeAgo(timestamp, { timeZone: this.userTimeZone })
    },

    formatTimestamp (timestamp) {
      return formatVersionHistoryTimestamp(timestamp, this.userTimeZone)
    },

    formatTimestampTitle (timestamp) {
      const formattedTimestamp = this.formatTimestamp(timestamp)
      if (!formattedTimestamp) return ''
      return `${formattedTimestamp} (${this.userTimeZone})`
    },

    formatPreview (text) {
      if (!text) return 'Empty version'
      const maxLength = 50
      // Remove markdown and HTML, then truncate
      const cleaned = text.replace(/[#*_~`[\]()]/g, '').replace(/<[^>]*>/g, '')
      return cleaned.substring(0, maxLength) + (cleaned.length > maxLength ? '...' : '')
    }
  },
  mounted () {
    this.loadVersions()
  }
}
